import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { brand, brandTextColor } from "../../../../config/theme";
import type { VehicleSocket } from "../../../../store/admin/vehiclesSocketStore";
import { useVehiclesSocketStore } from "../../../../store/admin/vehiclesSocketStore";
import { useFilteredSocketVehicles } from "../../../../store/admin/vehiclesSocketSelector";
import { useContainerWidth } from "../../../../hooks/useContainerWidth";
import { useCopyLocation } from "../../../../hooks/useCopyLocation";
import { liveSpeedKmh, readEngine } from "../Sidebar/engineDisplay";
import { shortAgo } from "../Sidebar/compactRow";
import { TONE_COLOR, connectionTone } from "../Sidebar/indicators";
import {
  ACTIONS_WIDTH,
  COLUMN_WIDTH,
  ROW_HEIGHT,
  exportRows,
  nextSort,
  sortVehicles,
  unitName,
  visibleColumns,
  visibleRange,
  type ColumnKey,
  type SortState,
} from "./unitsTable.ts";
import { UNIT_STATUS_COLORS as STATUS_COLORS, ENGINE_COLORS } from "../../../../constants/unitColors";

const NUMERIC: ColumnKey[] = ["speed", "odometer"];
const EXPORT_COLUMNS = ["name", "status", "speed", "engine", "lastReport", "odometer", "driver", "group", "location"] as const;

// El nombre se queda con el sobrante (minmax … 1fr): en una pantalla ancha la
// tabla no deja un hueco a la derecha y en una angosta el resto conserva su
// ancho, así cada columna cae en el mismo lugar en todos los renglones.
const template = (cols: ColumnKey[]) =>
  cols.map((c) => (c === "name" ? `minmax(${COLUMN_WIDTH.name}px, 1fr)` : `${COLUMN_WIDTH[c]}px`)).join(" ") +
  ` ${ACTIONS_WIDTH}px`;

interface Props {
  isDarkMode: boolean;
  onClose: () => void;
}

export default function UnitsTable({ isDarkMode, onClose }: Props) {
  const { t, i18n } = useTranslation("lite");
  const vehicles = useFilteredSocketVehicles() as VehicleSocket[];
  // timeTick avanza cada 30 s: re-dibuja el "hace cuánto" sin esperar un reporte.
  useVehiclesSocketStore((s) => s.timeTick);
  const focusedId = useVehiclesSocketStore((s) => s.focusedVehicleId);
  const setFocusedVehicleId = useVehiclesSocketStore((s) => s.setFocusedVehicleId);

  const [sort, setSort] = useState<SortState | null>(null);
  const [exporting, setExporting] = useState(false);
  const rows = useMemo(() => sortVehicles(vehicles, sort), [vehicles, sort]);

  const [widthRef, width] = useContainerWidth<HTMLDivElement>();
  const cols = useMemo(() => visibleColumns(width), [width]);
  const grid = useMemo(() => template(cols), [cols]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewport(el.clientHeight));
    ro.observe(el);
    setViewport(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  const { start, end } = visibleRange(scrollTop, viewport, rows.length);

  // Si la unidad se enfocó desde la lista o el mapa y su renglón no está a la
  // vista, la tabla la trae: las tres vistas cuentan lo mismo.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !focusedId) return;
    const i = rows.findIndex((r) => r.id === focusedId);
    if (i < 0) return;
    const top = i * ROW_HEIGHT;
    if (top < el.scrollTop || top + ROW_HEIGHT > el.scrollTop + el.clientHeight) {
      el.scrollTop = Math.max(0, top - el.clientHeight / 2 + ROW_HEIGHT / 2);
    }
    // Sólo cuando cambia la unidad enfocada; no en cada reporte del socket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId]);

  // exceljs pesa ~1 MB: se carga al exportar, no con el mapa.
  const exportExcel = async () => {
    setExporting(true);
    try {
      const { exportHistoricToExcel } = await import("../../../../pages/reports/utils/exportHistoric");
      const data = exportRows(
        rows,
        {
          status: (s) => t(`unitsTable.status.${s}`, t("unitsTable.status.offline")),
          engine: (e) =>
            e === "on" ? t("vehicleCard.statusOn") : e === "off" ? t("vehicleCard.statusOff") : t("vehicleCard.noData"),
        },
        (ms) => new Date(ms).toLocaleString(i18n.language),
      );
      const now = new Date();
      await exportHistoricToExcel(
        data,
        [...EXPORT_COLUMNS.map((key) => ({ key, header: t(`unitsTable.columns.${key}`) }))],
        {
          title: t("unitsTable.title"),
          period: `${t("unitsTable.count", { count: data.length })} · ${now.toLocaleString(i18n.language)}`,
          // Fecha local: toISOString() es UTC y después de las 18:00 en México ya daba el día siguiente.
          fileName: `unidades-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}.xlsx`,
        },
      );
    } finally {
      setExporting(false);
    }
  };

  const c = {
    panel: isDarkMode ? "bg-dark-bg border-dark-border" : "bg-white border-gray-200",
    head: isDarkMode ? "bg-dark-surface text-gray-400" : "bg-gray-50 text-gray-500",
    text: isDarkMode ? "text-gray-100" : "text-gray-900",
    sub: isDarkMode ? "text-gray-400" : "text-gray-500",
    line: isDarkMode ? "border-white/5" : "border-gray-100",
    hover: isDarkMode ? "hover:bg-white/5" : "hover:bg-gray-50",
    btn: isDarkMode ? "text-gray-400 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100",
  };

  return (
    <div ref={widthRef} className={`h-full flex flex-col border-t ${c.panel}`}>
      {/* Título: cuántas unidades y que lo que se ve es lo filtrado. */}
      <div className={`h-11 shrink-0 px-4 flex items-center gap-3 border-b ${c.line}`}>
        <i className={`ri-table-line text-base ${c.sub}`} aria-hidden />
        <h2 className={`text-sm font-semibold ${c.text}`}>{t("unitsTable.title")}</h2>
        <span className={`text-xs tabular-nums ${c.sub}`}>
          {t("unitsTable.count", { count: rows.length })}
        </span>
        <button
          type="button"
          onClick={exportExcel}
          disabled={exporting || rows.length === 0}
          title={t("unitsTable.exportTitle")}
          className={`text-xs rounded-md px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50 ${c.btn}`}
        >
          <i className={`${exporting ? "ri-loader-4-line animate-spin" : "ri-file-excel-2-line"}`} aria-hidden />
          {t("unitsTable.export")}
        </button>
        {sort && (
          <button type="button" onClick={() => setSort(null)} className={`text-xs rounded-md px-2 py-1 ${c.btn}`}>
            <i className="ri-close-line mr-1" aria-hidden />
            {t("unitsTable.clearSort")}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={t("unitsTable.close")}
          title={t("unitsTable.close")}
          className={`ml-auto w-8 h-8 flex items-center justify-center rounded-md ${c.btn}`}
        >
          <i className="ri-arrow-down-s-line text-lg" aria-hidden />
        </button>
      </div>

      <div
        ref={scrollRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        className="flex-1 min-h-0 overflow-auto"
        role="table"
        aria-rowcount={rows.length + 1}
      >
        {/* Encabezado fijo: la fila de nombres no se pierde al bajar. */}
        <div
          role="row"
          className={`sticky top-0 z-10 grid h-9 items-center border-b text-xs font-medium ${c.head} ${c.line}`}
          style={{ gridTemplateColumns: grid }}
        >
          {cols.map((col) => {
            const active = sort?.key === col;
            return (
              <button
                key={col}
                type="button"
                role="columnheader"
                aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}
                onClick={() => setSort((s) => nextSort(s, col))}
                className={`h-full px-3 flex items-center gap-1 select-none transition-colors ${
                  NUMERIC.includes(col) ? "justify-end" : ""
                } ${active ? c.text : ""} ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
              >
                <span className="truncate">{t(`unitsTable.columns.${col}`)}</span>
                <i
                  aria-hidden
                  className={`text-sm leading-none ${
                    active ? (sort!.dir === "asc" ? "ri-arrow-up-s-fill" : "ri-arrow-down-s-fill") : "ri-expand-up-down-line opacity-30"
                  }`}
                />
              </button>
            );
          })}
          <span role="columnheader" aria-label={t("unitsTable.columns.actions")} />
        </div>

        {rows.length === 0 ? (
          <p className={`px-4 py-8 text-sm text-center ${c.sub}`}>{t("unitsTable.empty")}</p>
        ) : (
          <div style={{ height: rows.length * ROW_HEIGHT, position: "relative" }}>
            {rows.slice(start, end).map((v, k) => (
              <Row
                key={v.id}
                v={v}
                index={start + k}
                cols={cols}
                grid={grid}
                focused={v.id === focusedId}
                onFocus={() => setFocusedVehicleId(v.id)}
                c={c}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface RowProps {
  v: VehicleSocket;
  index: number;
  cols: ColumnKey[];
  grid: string;
  focused: boolean;
  onFocus: () => void;
  c: Record<string, string>;
}

const Row = memo(function Row({ v, index, cols, grid, focused, onFocus, c }: RowProps) {
  const { t } = useTranslation("lite");
  const now = Date.now();
  const speed = liveSpeedKmh(v.speed, v.status);
  const engine = readEngine(v.speed, v.status, v.engineStatus).tone;
  const ago = shortAgo(v.lastReport?._seconds, now, t("compactList.now"));
  const fresh = connectionTone(v.lastReport?._seconds, now);
  const driver = (v as { driver?: { name?: string } }).driver?.name;
  const statusColor = STATUS_COLORS[v.status] || STATUS_COLORS.offline;

  const cell = (col: ColumnKey) => {
    switch (col) {
      case "name":
        return (
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusColor }} />
            <span className={`font-mono font-semibold text-[13px] truncate ${c.text}`}>{unitName(v)}</span>
          </span>
        );
      case "status":
        // El verde de marca como TEXTO no se lee sobre blanco (1.6:1):
        // brandTextColor baja a su tono oscuro en el tema claro.
        return (
          <span style={{ color: v.status === "driving" ? brandTextColor : statusColor }}>
            {t(`unitsTable.status.${v.status}`, t("unitsTable.status.offline"))}
          </span>
        );
      case "speed":
        return speed == null ? <span className={c.sub}>—</span> : <span className={c.text}>{Math.round(speed)} km/h</span>;
      case "engine":
        return (
          <span style={{ color: ENGINE_COLORS[engine] }}>
            {engine === "on"
              ? t("vehicleCard.statusOn")
              : engine === "off"
                ? t("vehicleCard.statusOff")
                : t("vehicleCard.noData")}
          </span>
        );
      case "lastReport":
        return (
          <span className={fresh === "gray" ? c.sub : ""} style={fresh === "gray" ? undefined : { color: TONE_COLOR[fresh] }}>
            {ago === "—" || ago === t("compactList.now") ? ago : t("unitsTable.ago", { time: ago })}
          </span>
        );
      case "odometer":
        return v.odometer != null ? (
          <span className={c.text}>{Math.round(v.odometer).toLocaleString()} km</span>
        ) : (
          <span className={c.sub}>—</span>
        );
      case "driver":
        return <span className={`truncate ${driver && driver !== "Sin asignar" ? c.text : c.sub}`}>{driver && driver !== "Sin asignar" ? driver : "—"}</span>;
      case "group":
        return <span className={`truncate ${v.group ? c.text : c.sub}`}>{v.group || "—"}</span>;
    }
  };

  return (
    <div
      role="row"
      aria-rowindex={index + 2}
      aria-selected={focused}
      tabIndex={0}
      onClick={onFocus}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onFocus())}
      className={`absolute left-0 right-0 grid items-center border-b text-xs tabular-nums cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 ${c.line} ${
        focused ? "" : c.hover
      }`}
      style={{
        top: index * ROW_HEIGHT,
        height: ROW_HEIGHT,
        gridTemplateColumns: grid,
        // La unidad seguida en el mapa: tinte de marca + filo a la izquierda,
        // el mismo lenguaje que el renglón activo del menú.
        ...(focused ? { backgroundColor: `${brand.primary}1F`, boxShadow: `inset 3px 0 0 ${brand.primary}` } : null),
      }}
    >
      {cols.map((col) => (
        <span
          key={col}
          role="cell"
          className={`px-3 min-w-0 flex items-center ${NUMERIC.includes(col) ? "justify-end" : ""}`}
        >
          {cell(col)}
        </span>
      ))}
      <span role="cell" className="flex items-center justify-center">
        <CopyCell lat={v.latitude} lng={v.longitude} btn={c.btn} />
      </span>
    </div>
  );
});

function CopyCell({ lat, lng, btn }: { lat: unknown; lng: unknown; btn: string }) {
  const { t } = useTranslation("lite");
  const { copy, state, available } = useCopyLocation(lat, lng);
  if (!available) return null;
  const label = state === "copied" ? t("quickActions.copied") : t("unitsTable.copy");
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void copy();
      }}
      aria-label={label}
      title={label}
      className={`w-7 h-7 flex items-center justify-center rounded-md ${state === "copied" ? "text-primary-500" : btn}`}
    >
      <i className={`${state === "copied" ? "ri-check-line" : state === "failed" ? "ri-error-warning-line" : "ri-file-copy-line"} text-sm`} />
    </button>
  );
}
